import { useCallback, useEffect, useRef } from "react";
import type { PttSidecarConfig } from "../../pttSidecarClient";
import { resolveWebSocketUrl } from "../../pttSidecarClient";
import { isSilentCapture, maxAudioPeak } from "./audioSignal";
import { floatToPcm16 } from "./pcm";
import type { PttTransport } from "./types";
import type { PttTurnSession } from "./usePttTurns";

const IDLE: PttTransport = {
  startRecording: () => undefined,
  stopRecording: () => undefined,
  isRecording: () => false,
  keyMode: "hold",
};

/** AWS Transcribe streaming over WebSocket. Toggle recording; live partial transcripts. */
export function useAwsStreamTransport(
  config: PttSidecarConfig | null,
  session: PttTurnSession,
): PttTransport {
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const wsRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const contextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const silenceRef = useRef<GainNode | null>(null);
  const turnIdRef = useRef<string | null>(null);
  const lastTranscriptRef = useRef("");
  const startSentRef = useRef(false);
  const stopRequestedRef = useRef(false);
  const startedAtRef = useRef(0);
  const maxPeakRef = useRef(0);
  const delayedStopRef = useRef<number | null>(null);

  const enabled = config?.provider === "aws";

  const clearDelayedStop = useCallback(() => {
    if (delayedStopRef.current !== null) {
      window.clearTimeout(delayedStopRef.current);
      delayedStopRef.current = null;
    }
  }, []);

  const stopAudio = useCallback(() => {
    processorRef.current?.disconnect();
    sourceRef.current?.disconnect();
    silenceRef.current?.disconnect();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    void contextRef.current?.close().catch(() => undefined);
    processorRef.current = null;
    sourceRef.current = null;
    silenceRef.current = null;
    streamRef.current = null;
    contextRef.current = null;
  }, []);

  const failSession = useCallback(
    (message: string, turnId?: string) => {
      stopAudio();
      wsRef.current?.close();
      wsRef.current = null;
      const pendingTurnId = turnId ?? turnIdRef.current;
      if (pendingTurnId) sessionRef.current.dropTurn(pendingTurnId);
      turnIdRef.current = null;
      lastTranscriptRef.current = "";
      startSentRef.current = false;
      stopRequestedRef.current = false;
      sessionRef.current.busyRef.current = false;
      sessionRef.current.setIdle();
      sessionRef.current.setError(message);
    },
    [stopAudio],
  );

  const finishSession = useCallback(
    async (transcript: string) => {
      const turnId = turnIdRef.current;
      turnIdRef.current = null;
      lastTranscriptRef.current = "";
      startSentRef.current = false;
      stopRequestedRef.current = false;
      stopAudio();
      wsRef.current?.close();
      wsRef.current = null;
      if (!turnId) return;
      sessionRef.current.setProcessing();
      try {
        await sessionRef.current.runQueryForTurn(turnId, transcript);
      } finally {
        sessionRef.current.busyRef.current = false;
        sessionRef.current.setIdle();
      }
    },
    [stopAudio],
  );

  const startRecording = useCallback(async () => {
    if (!enabled || !config || sessionRef.current.busyRef.current) return;
    sessionRef.current.busyRef.current = true;
    sessionRef.current.setError(null);
    sessionRef.current.setRecording();
    startedAtRef.current = Date.now();
    maxPeakRef.current = 0;
    const turnId = sessionRef.current.addPendingTurn("Listening...");
    turnIdRef.current = turnId;
    startSentRef.current = false;
    stopRequestedRef.current = false;

    try {
      const AudioContextCtor =
        window.AudioContext ??
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).webkitAudioContext;
      const audioContext = new AudioContextCtor({ sampleRate: config.sampleRateHz ?? 16000 });
      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      const ws = new WebSocket(resolveWebSocketUrl(config.baseUrl, "stream"));
      wsRef.current = ws;
      ws.binaryType = "arraybuffer";
      let ready = false;
      const queuedAudio: ArrayBuffer[] = [];
      const sendAudio = (chunk: ArrayBuffer) => {
        if (ws.readyState !== WebSocket.OPEN) return;
        if (!ready) {
          if (queuedAudio.length < 25) queuedAudio.push(chunk);
          return;
        }
        ws.send(chunk);
      };

      ws.onmessage = (event) => {
        const payload = JSON.parse(String(event.data)) as {
          type: "awaiting_start" | "ready" | "partial" | "final" | "done" | "error";
          transcript?: string;
          message?: string;
        };
        if (payload.type === "ready") {
          ready = true;
          while (queuedAudio.length > 0 && ws.readyState === WebSocket.OPEN) {
            const chunk = queuedAudio.shift();
            if (chunk) ws.send(chunk);
          }
        } else if (payload.type === "partial" || payload.type === "final") {
          const transcript = (payload.transcript ?? "").trim();
          if (transcript && turnIdRef.current) {
            lastTranscriptRef.current = transcript;
            sessionRef.current.updateTurnQuery(turnIdRef.current, transcript);
          }
        } else if (payload.type === "done") {
          void finishSession((payload.transcript ?? lastTranscriptRef.current).trim());
        } else if (payload.type === "error") {
          failSession(payload.message ?? "AWS voice stream failed", turnId);
        }
      };
      ws.addEventListener("error", () => {
        failSession("AWS voice stream failed", turnId);
      });
      await new Promise<void>((resolve, reject) => {
        ws.addEventListener("open", () => resolve(), { once: true });
        ws.addEventListener("error", () => reject(new Error("AWS voice stream failed")), { once: true });
      });

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      ws.send(JSON.stringify({ type: "start", sample_rate_hz: Math.round(audioContext.sampleRate) }));
      startSentRef.current = true;
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(2048, 1, 1);
      const silence = audioContext.createGain();
      silence.gain.value = 0;

      processor.onaudioprocess = (event) => {
        const input = event.inputBuffer.getChannelData(0);
        maxPeakRef.current = Math.max(maxPeakRef.current, maxAudioPeak(input));
        sendAudio(floatToPcm16(input));
      };

      source.connect(processor);
      processor.connect(silence);
      silence.connect(audioContext.destination);
      streamRef.current = stream;
      contextRef.current = audioContext;
      sourceRef.current = source;
      processorRef.current = processor;
      silenceRef.current = silence;

      if (stopRequestedRef.current) {
        clearDelayedStop();
        delayedStopRef.current = window.setTimeout(() => {
          delayedStopRef.current = null;
          stopAudio();
          if (ws.readyState === WebSocket.OPEN && startSentRef.current) {
            ws.send("end");
            sessionRef.current.setProcessing();
          }
        }, 250);
      }
    } catch (err) {
      stopAudio();
      wsRef.current?.close();
      wsRef.current = null;
      startSentRef.current = false;
      stopRequestedRef.current = false;
      sessionRef.current.dropTurn(turnId);
      sessionRef.current.busyRef.current = false;
      sessionRef.current.setIdle();
      sessionRef.current.setError(err instanceof Error ? err.message : "AWS voice stream failed");
    }
  }, [config, enabled, failSession, finishSession, stopAudio, clearDelayedStop]);

  const stopRecording = useCallback(() => {
    if (!enabled) return;
    const ws = wsRef.current;
    if (!ws && !streamRef.current) return;
    if (ws?.readyState === WebSocket.OPEN) {
      if (!startSentRef.current) {
        stopRequestedRef.current = true;
        sessionRef.current.setProcessing();
        return;
      }
      const durationMs = Date.now() - startedAtRef.current;
      if (streamRef.current && isSilentCapture(maxPeakRef.current, durationMs)) {
        failSession("No microphone signal detected");
        return;
      }
      stopAudio();
      ws.send("end");
      sessionRef.current.setProcessing();
    } else {
      stopRequestedRef.current = true;
      if (sessionRef.current.busyRef.current) {
        sessionRef.current.setProcessing();
      } else {
        sessionRef.current.setIdle();
      }
    }
  }, [enabled, failSession, stopAudio]);

  useEffect(() => {
    return () => {
      clearDelayedStop();
      stopAudio();
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [clearDelayedStop, stopAudio]);

  if (!enabled) return IDLE;

  return {
    startRecording,
    stopRecording,
    isRecording: () => session.pttState === "recording",
    keyMode: "hold",
  };
}
